import { Injectable } from '@angular/core';
import { Estimate } from 'src/app/models/Estimate';

@Injectable({
  providedIn: 'root'
})
export class DataService {

  private readonly AI_AUTO_REGENERATE_ON_RELOAD_KEY = 'aiAutoRegenerateOnReload';
  private readonly AI_LAST_FUNCTIONAL_FILE_PATH_KEY = 'aiLastFunctionalFilePath';
  private readonly AI_AUTO_REGENERATE_SELECTION_KEY = 'aiAutoRegenerateSelection';


  estimate: any;
  project: any;

  constructor() { }

  setEstimate(estimate: any) {
    //1. Store estimate into session
    sessionStorage.setItem('estimate', JSON.stringify(estimate));
  }

  getEstimate() {
    //1. Retrieve session data
    let data = sessionStorage.getItem('estimate');
    //2. Parse JSON 
    if (data) data = JSON.parse(data);
    //3. Return data
    return data as unknown as string;
  }

  getEstimateId() {
    //1. Retrieve session data
    const data = sessionStorage.getItem('estimate');
    if (data) {
      const parsed = JSON.parse(data);
      return parsed.id || null;
    }
    return null;
  }

  setTechnology(tech: any) {
    //1. Store tech into session
    sessionStorage.setItem('technology', JSON.stringify(tech));
  }

  getTechnology() {
    //1. Retrieve session data
    let data = sessionStorage.getItem('technology');
    //2. Parse JSON 
    if (data) data = JSON.parse(data);
    //3. Return data
    return data as unknown as any;
  }


  setProject(projectId: any) {
    //1. Store project into session
    sessionStorage.setItem('project', JSON.stringify(projectId));
  }

  getProject() {
    //1. Retrieve session data
    let data = sessionStorage.getItem('project');
    //2. Parse JSON 
    if (data) data = JSON.parse(data);
    //3. Return data
    return data;
  }

  setEstimativaNavigationContext(context: any) {
    sessionStorage.setItem('estimativaNavigationContext', JSON.stringify(context));
  }

  getEstimativaNavigationContext() {
    let data = sessionStorage.getItem('estimativaNavigationContext');
    if (data) data = JSON.parse(data);
    return data;
  }

  clearEstimativaNavigationContext() {
    sessionStorage.removeItem('estimativaNavigationContext');
  }

  setProjectsFilter(text: string) {
    if (text) {
      sessionStorage.setItem('projectsFilter', text);
    }
  }

  getProjectsFilter(): string {
    return sessionStorage.getItem('projectsFilter') ?? '';
  }

  clearProjectsFilter() {
    sessionStorage.removeItem('projectsFilter');
  }

  setAiGeneratedMacros(aiMacroActivities: any) {
    sessionStorage.setItem('aiMacros', JSON.stringify(aiMacroActivities));
  }

  getAiGeneratedMacros() {
    const data = sessionStorage.getItem('aiMacros');
    if (!data) {
      return null;
    }

    try {
      const parsedData = JSON.parse(data);
      return parsedData;
    } catch {
      return null;
    }
  }

  clearAiGeneratedMacros() {
    sessionStorage.removeItem('aiMacros');
  }

  setAiGeneratedActionItems(aiActionitems: any) {
    sessionStorage.setItem('aiActionItems', JSON.stringify(aiActionitems));
  }

  getAiGeneratedActionItems() {
    const data = sessionStorage.getItem('aiActionItems');
    if (!data) {
      return null;
    }

    try {
      const parsedData = JSON.parse(data);
      return parsedData;
    } catch {
      return null;
    }
  }

  clearAiGeneratedActionItems() {
    sessionStorage.removeItem('aiActionItems');
  }

  setAiModifiedActionItems(aiActionitems: any) {
    sessionStorage.setItem('aiModifiedActionItems', JSON.stringify(aiActionitems));
  }

  getAiModifiedActionItems() {
    const data = sessionStorage.getItem('aiModifiedActionItems');
    if (!data) {
      return null;
    }

    try {
      const parsedData = JSON.parse(data);
      return parsedData;
    } catch {
      return null;
    }
  }

  clearAiModifieddActionItems() {
    sessionStorage.removeItem('aiModifiedActionItems');
  }

  setShowAiMacroWarning(show: boolean) {
    sessionStorage.setItem('showAiMacroWarning', JSON.stringify(show));
  }

  getShowAiMacroWarning() {
    const data = sessionStorage.getItem('showAiMacroWarning');
    if (!data) {
      return false;
    }

    try {
      const parsedData = JSON.parse(data);
      return parsedData;
    } catch {
      return false;
    }
  }

  clearShowAiMacroWarning() {
    sessionStorage.removeItem('showAiMacroWarning');
  }

  requestAiAutoRegenerateOnReload() {
    sessionStorage.setItem(this.AI_AUTO_REGENERATE_ON_RELOAD_KEY, JSON.stringify(true));
  }

  setAiAutoRegenerateSelection(selection: { methodology: string; estimationModel: string }) {
    sessionStorage.setItem(this.AI_AUTO_REGENERATE_SELECTION_KEY, JSON.stringify(selection));
  }

  consumeAiAutoRegenerateSelection(): { methodology: string; estimationModel: string } | null {
    const data = sessionStorage.getItem(this.AI_AUTO_REGENERATE_SELECTION_KEY);
    sessionStorage.removeItem(this.AI_AUTO_REGENERATE_SELECTION_KEY);

    if (!data) {
      return null;
    }

    try {
      const parsed = JSON.parse(data);
      return {
        methodology: parsed?.methodology || '',
        estimationModel: parsed?.estimationModel || ''
      };
    } catch {
      return null;
    }
  }

  clearAiAutoRegenerateSelection() {
    sessionStorage.removeItem(this.AI_AUTO_REGENERATE_SELECTION_KEY);
  }

  consumeAiAutoRegenerateOnReloadRequest(): boolean {
    const data = sessionStorage.getItem(this.AI_AUTO_REGENERATE_ON_RELOAD_KEY);
    sessionStorage.removeItem(this.AI_AUTO_REGENERATE_ON_RELOAD_KEY);

    if (!data) {
      return false;
    }

    try {
      return JSON.parse(data) === true;
    } catch {
      return false;
    }
  }

  setLastAiFunctionalFilePath(filePath: string) {
    sessionStorage.setItem(this.AI_LAST_FUNCTIONAL_FILE_PATH_KEY, filePath);
  }

  getLastAiFunctionalFilePath(): string | null {
    return sessionStorage.getItem(this.AI_LAST_FUNCTIONAL_FILE_PATH_KEY);
  }

  clearLastAiFunctionalFilePath() {
    sessionStorage.removeItem(this.AI_LAST_FUNCTIONAL_FILE_PATH_KEY);
  }
}

